import React, { useEffect, useRef, useState } from "react";
import { faDownload, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Box,
  Button,
  Card,
  IconButton,
  Modal,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { useReactToPrint } from "react-to-print";

// Same constants as BrandAuditMetricDetails — width only, no fixed height
const CARD_W     = 230;
const FONT_SIZE  = "11px";
const PRINT_COLS_PER_PAGE = 3;
const FIXED_HEADER_COLS   = 1;
// Fixed column widths (via <colgroup> + table-layout:fixed) so the header
// row (brand logo/name) always lines up exactly with the data columns below,
// regardless of each column's own CSS class widths.
const ATTRIBUTE_COL_W = 150;
const BRAND_COL_W     = CARD_W + 24;

// ── Helpers ─────────────────────────────────────────────────────────────────

function filterHeaderForBrands(headEl, fullBrandOrder = [], subsetBrands = []) {
  if (!headEl || !subsetBrands.length) return headEl;
  const keepIdxs = subsetBrands
    .map((b) => fullBrandOrder.indexOf(b))
    .filter((i) => i >= 0);
  const headChildren = React.Children.toArray(headEl.props.children);
  if (!headChildren.length) return headEl;
  const rowEl   = headChildren[0];
  const cells   = React.Children.toArray(rowEl.props.children);
  const filtered = cells.filter(
    (_, i) => i < FIXED_HEADER_COLS || keepIdxs.includes(i - FIXED_HEADER_COLS)
  );
  return React.cloneElement(headEl, {}, React.cloneElement(rowEl, {}, filtered));
}

function resolveScore(bs) {
  const raw = bs?.score ?? bs?.rating;
  if (raw == null || raw === "") return null;
  const n = typeof raw === "number" ? raw : Number(raw);
  return isNaN(n) ? null : n;
}

function scoreColor(n) {
  // Match the color to the rounded whole number actually shown on screen
  const displayedN = n != null ? Number(Number(n).toFixed(0)) : null;
  if (displayedN >= 9) return "#198754";
  if (displayedN >= 7) return "#2196F3";
  return "#dc3545";
}

// ── Cell components ──────────────────────────────────────────────────────────

// Interactive cell — Card with auto height (no clipping), same look as BrandAudit detail view
function DataCell({ brandScore, classes }) {
  const scoreNum = resolveScore(brandScore);
  return (
    <TableCell className={classes.attribute_score} sx={{ verticalAlign: "top", padding: "4px 6px" }}>
      {scoreNum != null ? (
        <Card sx={{ width: CARD_W, p: "8px 10px" }}>
          <Typography
            variant="body1"
            sx={{ padding: "1px", fontWeight: "bold", color: scoreColor(scoreNum) }}
          >
            {scoreNum.toFixed(0)}
          </Typography>
          <Typography sx={{ fontSize: FONT_SIZE, mt: 0.4, lineHeight: 1.55 }}>
            {brandScore?.explanation}
          </Typography>
        </Card>
      ) : (
        <Card sx={{ width: CARD_W, minHeight: 40, display: "flex", justifyContent: "center", alignItems: "center" }}>
          <Typography>-</Typography>
        </Card>
      )}
    </TableCell>
  );
}

// PDF cell — same layout, slightly tighter sizing
function PdfCell({ brandScore, classes }) {
  const scoreNum = resolveScore(brandScore);
  return (
    <TableCell
      className={classes.attribute_score}
      style={{ verticalAlign: "top", padding: "3px 4px" }}
    >
      {scoreNum != null ? (
        <div style={{ width: CARD_W, padding: "4px 6px" }}>
          <div style={{ fontWeight: "bold", color: scoreColor(scoreNum), fontSize: "10px", marginBottom: 2 }}>
            {scoreNum.toFixed(0)}
          </div>
          <div style={{ fontSize: "8px", lineHeight: 1.4, color: "#333" }}>
            {brandScore?.explanation}
          </div>
        </div>
      ) : (
        <div style={{ width: CARD_W, textAlign: "center", fontSize: "8px", color: "#aaa" }}>-</div>
      )}
    </TableCell>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

/**
 * CategoryAnalysis-only tabbed Detailed Table modal.
 * LLM tabs at top; Download as PDF prints only the active tab with full text.
 * Does NOT affect any other module.
 */
export default function CADetailedTableModal({ show, onClose, tabs = [] }) {
  const [activeTab, setActiveTab] = useState(0);
  const printRef = useRef();

  useEffect(() => {
    if (show) setActiveTab(0);
  }, [show]);

  const safeIdx       = Math.min(activeTab, Math.max(tabs.length - 1, 0));
  const tab           = tabs[safeIdx];
  const data          = tab?.data          || [];
  const headers       = tab?.headers       || [];
  const renderHeaders = tab?.renderHeaders;
  const formatName    = tab?.formatName    || ((s) => s);
  const pdfTitle      = tab?.pdfTitle      || "Detailed Table";
  const classes       = tab?.classes       || {};

  const hasData =
    Array.isArray(data)    && data.length    > 0 &&
    Array.isArray(headers) && headers.length > 0;

  const fullHeaderEl = typeof renderHeaders === "function" ? renderHeaders() : null;

  const exportToPDF = useReactToPrint({
    contentRef: printRef,
    documentTitle: pdfTitle,
    removeAfterPrint: true,
    pageStyle: `
      @page { size: 380mm auto; margin: 12mm 8mm 8mm; }
      @media print {
        body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        table { border-collapse: collapse; width: 100%; }
        td, th { vertical-align: top; }
      }
    `,
  });

  if (!show) return null;

  return (
    <Modal open={show} onClose={onClose} id="ca_metric_detail_llm">
      <Box
        sx={{
          width: "90%",
          maxWidth: "1400px",
          backgroundColor: "#fff",
          boxShadow: 24,
          p: 3,
          borderRadius: 2,
          position: "absolute",
          top: "5vh",
          left: "50%",
          transform: "translateX(-50%)",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* LLM Tabs */}
        {tabs.length > 1 && (
          <Tabs
            value={safeIdx}
            onChange={(_, i) => setActiveTab(i)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              mb: 2,
              borderBottom: "2px solid #e8edf7",
              flexShrink: 0,
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.84rem",
                minHeight: 40,
                padding: "6px 20px",
                color: "#6b7a9d",
              },
              "& .Mui-selected": { color: "#4361ee" },
              "& .MuiTabs-indicator": {
                backgroundColor: "#4361ee",
                height: 3,
                borderRadius: "3px 3px 0 0",
              },
            }}
          >
            {tabs.map((t, i) => <Tab key={i} label={t.label} />)}
          </Tabs>
        )}

        {/* Header row */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexShrink: 0,
          }}
        >
          <Typography variant="h6">Detailed Table View</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={exportToPDF}
              disabled={!hasData}
              sx={{ textTransform: "none" }}
            >
              <FontAwesomeIcon icon={faDownload} style={{ marginRight: "5px" }} />
              Download as PDF
            </Button>
            <IconButton onClick={onClose}>
              <FontAwesomeIcon icon={faTimes} />
            </IconButton>
          </Box>
        </Box>

        {/* Table */}
        {!hasData ? (
          <Box sx={{ minHeight: 240, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Typography>Data Not Available</Typography>
          </Box>
        ) : (
          <div style={{ flex: 1, overflowY: "auto", overflowX: "auto", paddingBottom: 8 }}>
            <Box sx={{ pb: 4 }}>
              <Table sx={{ tableLayout: "fixed" }}>
                <colgroup>
                  <col style={{ width: ATTRIBUTE_COL_W }} />
                  {headers.map((_, i) => (
                    <col key={i} style={{ width: BRAND_COL_W }} />
                  ))}
                </colgroup>
                {fullHeaderEl}
                <TableBody>
                  {data.map((mainItem, index) => (
                    <TableRow key={index}>
                      {/* Attribute column — compact, no excess padding */}
                      <TableCell
                        className={classes.attribute_headings}
                        sx={{
                          verticalAlign: "middle",
                          padding: "6px 10px",
                          fontWeight: 600,
                          fontSize: "0.82rem",
                        }}
                      >
                        {formatName(mainItem.name)}
                      </TableCell>

                      {headers.map((brand, idx) => {
                        const detailInfo = mainItem?.scores || mainItem?.brands;
                        const brandScore = detailInfo?.find(
                          (item) => item?.name?.toLowerCase() === brand?.toLowerCase()
                        );
                        return (
                          <DataCell key={idx} brandScore={brandScore} classes={classes} />
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </div>
        )}

        {/* Hidden PDF print target — active tab only, paginated by brand columns */}
        {hasData && (
          <span style={{ display: "none" }}>
            <div ref={printRef} style={{ background: "#fff", fontFamily: "Arial, sans-serif" }}>
              {Array.from({ length: Math.ceil(headers.length / PRINT_COLS_PER_PAGE) }).map(
                (_, pageNum) => {
                  const colStart   = pageNum * PRINT_COLS_PER_PAGE;
                  const colEnd     = Math.min(headers.length, colStart + PRINT_COLS_PER_PAGE);
                  const pageBrands = headers.slice(colStart, colEnd);
                  const filteredHdr = filterHeaderForBrands(fullHeaderEl, headers, pageBrands);
                  const isLast     = colEnd >= headers.length;
                  return (
                    <div
                      key={pageNum}
                      style={{
                        breakAfter: isLast ? "auto" : "page",
                        pageBreakAfter: isLast ? "auto" : "always",
                      }}
                    >
                      <Table style={{ width: "100%", borderCollapse: "collapse" }}>
                        {filteredHdr}
                        <TableBody>
                          {data.map((mainItem, rIdx) => (
                            <TableRow key={rIdx}>
                              <TableCell
                                className={classes.attribute_headings}
                                style={{
                                  verticalAlign: "middle",
                                  padding: "4px 8px",
                                  fontWeight: 600,
                                  fontSize: "9px",
                                  width: 100,
                                  maxWidth: 120,
                                }}
                              >
                                {formatName(mainItem.name)}
                              </TableCell>
                              {pageBrands.map((brand) => {
                                const detailInfo = mainItem?.scores || mainItem?.brands || [];
                                const brandScore = detailInfo?.find(
                                  (item) => item?.name?.toLowerCase() === brand?.toLowerCase()
                                );
                                return (
                                  <PdfCell
                                    key={`${mainItem.name}-${brand}`}
                                    brandScore={brandScore}
                                    classes={classes}
                                  />
                                );
                              })}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  );
                }
              )}
            </div>
          </span>
        )}
      </Box>
    </Modal>
  );
}
